import { useState } from 'react'
import { useNavigate } from 'react-router'
import { AppBar, Toolbar, Typography, Button, FormControlLabel, Switch, Menu, MenuItem, Divider } from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import VisibilityIcon from '@mui/icons-material/Visibility'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import LogoutIcon from '@mui/icons-material/Logout'

interface EditorAppBarProps {
  careerName: string
  confirmationMode: boolean
  autoSave: boolean
  saving: boolean
  userName: string | null
  onAutoSaveChange: (enabled: boolean) => void
  onSave: () => void
  onAIGenerate: () => void
  onPreview: () => void
  onConfirmAI: () => void
  onDiscardAI: () => void
  onSupportOpen: () => void
}

export function EditorAppBar({
  careerName,
  confirmationMode,
  autoSave,
  saving,
  userName,
  onAutoSaveChange,
  onSave,
  onAIGenerate,
  onPreview,
  onConfirmAI,
  onDiscardAI,
  onSupportOpen,
}: EditorAppBarProps) {
  const navigate = useNavigate()
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null)

  return (
    <AppBar position="sticky">
      <Toolbar>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          PRシートエディタ - {careerName}
        </Typography>
        {confirmationMode ? (
          <>
            <Typography variant="body1" sx={{ color: 'white', mr: 2 }}>
              この内容で確定しますか？
            </Typography>
            <Button color="inherit" variant="outlined" onClick={onDiscardAI} sx={{ mr: 2, borderColor: 'white' }}>
              破棄
            </Button>
            <Button
              color="inherit"
              variant="contained"
              onClick={onConfirmAI}
              sx={{
                mr: 2,
                bgcolor: 'white',
                color: 'primary.main',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' },
              }}
            >
              確定
            </Button>
          </>
        ) : (
          <>
            {!autoSave && (
              <Button color="inherit" startIcon={<SaveIcon />} onClick={onSave} disabled={saving} sx={{ mr: 2 }}>
                {saving ? '保存中...' : '保存'}
              </Button>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={autoSave}
                  onChange={(e) => onAutoSaveChange(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: 'white',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: 'rgba(255, 255, 255, 0.5)',
                    },
                  }}
                />
              }
              label="自動保存"
              sx={{
                color: 'white',
                mr: 2,
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                },
              }}
            />
            <Button color="inherit" startIcon={<AutoFixHighIcon />} onClick={onAIGenerate} sx={{ ml: 1 }}>
              AI 生成
            </Button>
            <Button color="inherit" startIcon={<VisibilityIcon />} onClick={onPreview} sx={{ ml: 1 }}>
              プレビュー
            </Button>
          </>
        )}
        <Button
          color="inherit"
          startIcon={<AccountCircleIcon />}
          endIcon={<KeyboardArrowDownIcon />}
          onClick={(e) => setUserMenuAnchor(e.currentTarget)}
          sx={{ ml: 2 }}
        >
          {userName || 'ユーザー'}
        </Button>
        <Menu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={() => setUserMenuAnchor(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem
            onClick={() => {
              setUserMenuAnchor(null)
              onSupportOpen()
            }}
          >
            <HelpOutlineIcon sx={{ mr: 1 }} />
            サポート
          </MenuItem>
          <MenuItem
            component="a"
            href="https://www.notion.so/lifeistech/PR-2a8dc408133980cb88e6c702f75ab358"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setUserMenuAnchor(null)}
          >
            <MenuBookIcon sx={{ mr: 1 }} />
            利用手順
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => {
              setUserMenuAnchor(null)
              navigate('/logout')
            }}
          >
            <LogoutIcon sx={{ mr: 1 }} />
            ログアウト
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  )
}
